import React from "react";
import PageHeader from "../components/PageHeader";
import ImageTile from "../components/ImageTile";
import { globalImages } from "../data/images";

const regions = [
  { name: "Middle East",    ports: ["Dubai", "Jeddah", "Doha", "Muscat"],        flag: "🕌", accent: "from-amber-300 to-orange-500" },
  { name: "Europe",         ports: ["Rotterdam", "Hamburg", "Antwerp", "Genoa"], flag: "🇪🇺", accent: "from-indigo-300 to-indigo-600" },
  { name: "South East Asia",ports: ["Singapore", "Kuala Lumpur", "Bangkok"],     flag: "🌏", accent: "from-slate-300 to-slate-500" },
  { name: "North America",  ports: ["New York", "Los Angeles", "Toronto"],       flag: "🗽", accent: "from-blue-300 to-blue-600" },
  { name: "Africa",         ports: ["Mombasa", "Durban", "Lagos"],               flag: "🌍", accent: "from-amber-400 to-amber-700" },
  { name: "Oceania",        ports: ["Sydney", "Melbourne", "Auckland"],          flag: "🦘", accent: "from-cyan-300 to-blue-500" },
];

const marqueeCountries = [
  "🇦🇪 UAE","🇸🇦 Saudi Arabia","🇶🇦 Qatar","🇴🇲 Oman","🇳🇱 Netherlands",
  "🇩🇪 Germany","🇬🇧 United Kingdom","🇺🇸 United States","🇨🇦 Canada",
  "🇸🇬 Singapore","🇲🇾 Malaysia","🇦🇺 Australia","🇳🇿 New Zealand",
  "🇯🇵 Japan","🇰🇷 South Korea","🇰🇪 Kenya","🇿🇦 South Africa",
  "🇫🇷 France","🇮🇹 Italy","🇪🇸 Spain","🇧🇪 Belgium",
];

export default function GlobalPage() {
  return (
    <>
      <PageHeader eyebrow="Global Reach"
        title="One supply chain," titleAccent="every continent."
        subtitle="We ship to importers, retailers, hotel chains, and food-service distributors across six continents — from single FCL consignments to long-term annual contracts."
        image={globalImages.port} emoji="🚢"
        rightContent={
          <div className="glass-strong shimmer-border rounded-3xl p-6 w-full max-w-sm">
            <div className="text-[11px] uppercase tracking-[0.22em] text-stone-400 mb-5">Trade Footprint</div>
            <div className="grid grid-cols-2 gap-3 mb-5">
              {[{v:"30+",l:"Countries",icon:"🌐"},{v:"6",l:"Continents",icon:"🗺️"},{v:"250+",l:"Shipments / yr",icon:"🚢"},{v:"12",l:"Active Ports",icon:"⚓"}].map(s => (
                <div key={s.l} className="glass rounded-2xl p-4 text-center">
                  <div className="text-2xl mb-1">{s.icon}</div>
                  <div className="font-display text-2xl font-semibold text-gradient">{s.v}</div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-400 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-2">Primary Gateways</div>
            <div className="flex flex-wrap gap-1.5">
              {["Nhava Sheva","Mundra","Colombo","Jebel Ali"].map(p => (
                <span key={p} className="glass-pill rounded-full px-3 py-1 text-xs text-stone-300">{p}</span>
              ))}
            </div>
          </div>
        } />

      {/* Stats */}
      <section className="relative mt-16 md:mt-16 z-10">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-[1600px] mx-auto">
          <div className="glass-strong shimmer-border rounded-3xl p-6 md:p-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { v: "30+",  l: "Countries" },
              { v: "250+", l: "Shipments / yr" },
              { v: "6",    l: "Continents" },
              { v: "12",   l: "Active ports" },
            ].map((s, i) => (
              <div key={s.l} className={`text-center ${i !== 0 ? "md:border-l md:border-stone-900/10" : ""} px-2`}>
                <div className="font-display text-4xl md:text-5xl font-semibold text-gradient">{s.v}</div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regions */}
      <section className="relative py-24 md:py-32">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-[1600px] mx-auto">
          <div className="max-w-2xl mb-12">
            <div className="inline-flex items-center gap-2 glass-pill rounded-full px-4 py-1.5 mb-5">
              <span className="text-[11px] uppercase tracking-[0.22em] text-amber-700">Regional Coverage</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-semibold leading-tight text-stone-500">
              Active lanes, <span className="text-gradient">live volumes.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {regions.map((r, i) => (
              <div key={r.name} className="group glass rounded-3xl p-7 glass-hover fade-up" style={{ animationDelay: `${i * 0.06}s` }}>
                <div className="flex items-center gap-4 mb-5">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${r.accent} flex items-center justify-center text-2xl shadow-lg`}>
                    {r.flag}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-400">Region</div>
                    <div className="font-display text-xl text-stone-500">{r.name}</div>
                  </div>
                </div>
                <div className="text-[11px] uppercase tracking-widest text-stone-400 mb-2">Key Ports</div>
                <div className="flex flex-wrap gap-1.5">
                  {r.ports.map(p => (
                    <span key={p} className="glass-pill rounded-full px-3 py-1 text-xs text-stone-600">{p}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Port banner */}
      <section className="relative py-5">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-[1600px] mx-auto">
          <div className="relative rounded-[2rem] overflow-hidden h-[320px] md:h-[420px]">
            <ImageTile src={globalImages.containers} alt="Port containers" emoji="🚢"
              className="absolute inset-0"
              overlayClassName="bg-gradient-to-r from-[#0d1f2d] via-[#0d1f2d]/55 to-transparent" />
            <div className="relative h-full flex items-center p-10 md:p-16">
              <div className="max-w-xl">
                <h3 className="font-display text-3xl md:text-5xl font-semibold leading-tight text-stone-500">
                  From Nhava Sheva, <span className="text-gradient">to your port.</span>
                </h3>
                <p className="mt-4 text-stone-600">
                  Primary sailings via Nhava Sheva (JNPT) and Mundra — with transshipments through Colombo, Jebel Ali, and Singapore for onward routing worldwide.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Country marquee */}
      <section className="relative py-20 overflow-hidden">
        <div className="max-w-2xl mx-auto text-center mb-10 px-4">
          <h2 className="font-display text-3xl md:text-4xl font-semibold">
            <span className="text-gradient">Where our produce lands.</span>
          </h2>
        </div>
        <div className="relative w-full overflow-hidden rounded-2xl mx-auto max-w-7xl px-4 md:px-6">
          <div className="w-fit flex gap-4 animate-marquee whitespace-nowrap" style={{ maskImage: "linear-gradient(90deg, transparent 0%, black 10%, black 90%, transparent 100%)" }}>
            {[...marqueeCountries, ...marqueeCountries].map((c, i) => (
              <div key={i} className="glass-pill rounded-full px-5 py-3 text-sm text-stone-600 shrink-0">{c}</div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
